<template>
  <section
    v-if="props.walletAddress"
    class="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden"
  >
    <!-- Header -->
    <button
      type="button"
      class="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
      @click="isExpanded = !isExpanded"
    >
      <span class="text-sm font-semibold text-[#0e141b]">Recent Swaps</span>
      <svg
        class="w-4 h-4 text-gray-400 transition-transform duration-200"
        :class="isExpanded && 'rotate-180'"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <!-- Content -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 max-h-0"
      enter-to-class="opacity-100 max-h-[600px]"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 max-h-[600px]"
      leave-to-class="opacity-0 max-h-0"
    >
      <div v-if="isExpanded" class="border-t border-gray-200">
        <RecentSwapsList
          ref="swapListRef"
          :swaps="swaps"
          :is-loading="isLoading"
          :continuation-states="continuationStates"
          @resume-ton-leg="handleResumeTonLeg"
        />
      </div>
    </Transition>
  </section>
</template>

<script setup lang="ts">
import { ref, toRef } from 'vue'
import { refreshRecentSwaps, useRecentSwaps, type RecentSwap } from './use-recent-swaps'
import { useRecentSwapContinuation } from './use-recent-swap-continuation'
import RecentSwapsList from './RecentSwapsList.vue'

interface SwapListRef {
  resetResuming: () => void
}

const props = defineProps<{
  walletAddress?: string
}>()

const isExpanded = ref(false)
const swapListRef = ref<SwapListRef | null>(null)

const walletAddress = toRef(props, 'walletAddress')
const { swaps, isLoading } = useRecentSwaps()
const { continuationStates, resume } = useRecentSwapContinuation(walletAddress, isExpanded)

const handleResumeTonLeg = async (swap: RecentSwap) => {
  try {
    await resume(swap)
  } catch (error) {
    if (import.meta.env.DEV) console.error('[RecentSwapsInline] Resume failed:', error)
  } finally {
    refreshRecentSwaps()
    swapListRef.value?.resetResuming()
  }
}
</script>
